import { supabase } from '@/lib/supabase'

// ── Bracket data loading ──

export async function fetchBracketWithDetails(bracketId) {
  const { data, error } = await supabase.rpc('get_bracket_with_full_details', {
    p_bracket_id: bracketId
  })
  if (error) throw error
  return data
}

export async function fetchTournamentBracketsWithEntries(tournamentId) {
  const { data, error } = await supabase
    .from('brackets')
    .select(`
      id,
      division_id,
      status,
      format,
      divisions (
        id,
        name,
        gender,
        team_size
      ),
      bracket_entries!bracket_entries_bracket_id_fkey (
        id,
        user_id,
        group_id
      )
    `)
    .eq('tournament_id', tournamentId)
    .order('division_id')
  if (error) throw error
  return data
}

export async function fetchTournamentBrackets(tournamentId) {
  const { data, error } = await supabase
    .from('brackets')
    .select('id, division_id, divisions ( id, name, gender, team_size )')
    .eq('tournament_id', tournamentId)
    .order('division_id')
  if (error) throw error
  return data
}

// ── Entries ──

export async function fetchEntries(userId, bracketId) {
  const { data, error } = await supabase.rpc('get_entries', {
    p_user_id: userId,
    p_bracket_id: bracketId
  })
  if (error) throw error
  return data
}

export async function fetchEntryById(entryId) {
  const { data, error } = await supabase.rpc('get_entry_by_id', {
    p_entry_id: entryId
  })
  if (error) throw error
  return data
}

export async function createEntry(bracketId, isOfficial, groupId) {
  const { data, error } = await supabase.rpc('create_entry', {
    p_bracket_id: bracketId,
    p_is_official: isOfficial,
    p_group_id: groupId
  })
  if (error) throw error
  return data
}

export async function saveEntry(entryId, picks) {
  const { data, error } = await supabase.rpc('save_entry', {
    p_entry_id: entryId,
    p_picks: picks
  })
  if (error) throw error
  return data
}

// ── Per-round locking (multi-week brackets) ──

export async function fetchBracketRounds(bracketId) {
  const { data, error } = await supabase
    .from('bracket_rounds')
    .select('round, label, status, submission_deadline, matchups_published, auto_locked_at')
    .eq('bracket_id', bracketId)
    .order('round')
  if (error) throw error
  return data
}

/**
 * Upsert one round's lock state. Omitted fields keep their current value, so
 * this doubles as "open round 2" and "move round 2's deadline".
 * @param {'pending'|'open'|'locked'} [status]
 * @param {string|null} [deadline] ISO string; pass clearDeadline to unset it
 */
export async function setBracketRound(bracketId, round, { status, deadline, label, clearDeadline } = {}) {
  const { data, error } = await supabase.rpc('set_bracket_round', {
    p_bracket_id: bracketId,
    p_round: round,
    p_status: status ?? null,
    p_submission_deadline: deadline ?? null,
    p_label: label ?? null,
    p_clear_deadline: !!clearDeadline,
  })
  if (error) throw error
  return data
}

/**
 * Write the league-assigned matchups for a round and flag them as published,
 * so the bracket renders them verbatim instead of deriving participants from
 * each user's previous-round picks.
 * @param {Array<{match_number:number, team_one_id:string|null, team_two_id:string|null}>} matchups
 */
export async function publishRoundMatchups(bracketId, round, matchups) {
  const { data, error } = await supabase.rpc('publish_bracket_round_matchups', {
    p_bracket_id: bracketId,
    p_round: round,
    p_matchups: matchups,
  })
  if (error) throw error
  return data
}

// ── Official entry lookup ──

export async function fetchOfficialEntry(bracketId) {
  const { data, error } = await supabase
    .from('bracket_entries')
    .select('id')
    .eq('bracket_id', bracketId)
    .is('user_id', null)
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function updateBracketOfficialEntryId(bracketId, officialEntryId) {
  const { data, error } = await supabase
    .from('brackets')
    .update({ official_entry_id: officialEntryId })
    .eq('id', bracketId)
  if (error) throw error
  return data
}

// ── User profiles ──

export async function fetchUserFirstName(userId) {
  // Also pulls the avatar fields so the picks page can render a clickable
  // avatar linking to the entry owner's profile.
  const { data, error } = await supabase
    .from('user_profiles')
    .select('first_name, full_name, avatar_url, avatar_type')
    .eq('id', userId)
    .single()
  if (error) throw error
  return data
}

// ── Groups ──

export async function fetchGroups(userId) {
  const { data, error } = await supabase.rpc('get_groups', {
    p_user_id: userId
  })
  if (error) throw error
  return data
}

export async function fetchGroupsForBracket(bracketId, userId) {
  const { data, error } = await supabase.rpc('get_groups_for_bracket', {
    p_bracket_id: bracketId,
    p_user_id: userId
  })
  if (error) throw error
  return data
}

export async function addBracketToGroup(groupId, bracketId) {
  const { data, error } = await supabase.rpc('add_bracket_to_group', {
    p_group_id: groupId,
    p_bracket_id: bracketId
  })
  if (error) throw error
  return data
}

export async function removeBracketFromGroup(groupId, bracketId) {
  const { data, error } = await supabase.rpc('remove_bracket_from_group', {
    p_group_id: groupId,
    p_bracket_id: bracketId
  })
  if (error) throw error
  return data
}

// ── Leaderboard ──

export async function fetchLeaderboardRankings({ groupId, bracketId, limit = 100 }) {
  const { data, error } = await supabase.rpc('get_leaderboard_rankings', {
    p_group_id: groupId === 'global' ? null : groupId,
    p_sport_id: null,
    p_tournament_id: null,
    p_division_id: null,
    p_bracket_id: bracketId,
    p_year: null,
    p_limit: limit
  })
  if (error) throw error
  return data
}

// ── Bracket status ──

export async function updateBracketStatus(bracketId, status) {
  const { data, error } = await supabase
    .from('brackets')
    .update({ status })
    .eq('id', bracketId)
  if (error) throw error
  return data
}

// ── Bracket population (admin) ──

export async function deleteSeeds(seedIds) {
  const { data, error } = await supabase
    .from('seeds')
    .delete()
    .in('id', seedIds)
  if (error) throw error
  return data
}

export async function deleteBracketTeams(teamIds) {
  const { data, error } = await supabase
    .from('bracket_teams')
    .delete()
    .in('id', teamIds)
  if (error) throw error
  return data
}

export async function saveBracketData(bracketId, playersData, matchesData) {
  const { data, error } = await supabase.rpc('save_bracket_data', {
    bracket_id_param: bracketId,
    players_data: playersData,
    matches_data: matchesData
  })
  if (error) throw error
  return data
}

export async function savePoolBracketData(bracketId, pools, openBracket = false) {
  const { data, error } = await supabase.rpc('save_pool_bracket_data', {
    p_bracket_id: bracketId,
    p_pools: pools,
    p_open_bracket: openBracket,
  })
  if (error) throw error
  return data
}

export async function savePoolTiebreaks(entryId, tiebreaks) {
  const { data, error } = await supabase.rpc('save_pool_tiebreaks', {
    p_entry_id: entryId,
    p_tiebreaks: tiebreaks,
  })
  if (error) throw error
  return data
}
